import { useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SampleModalContext, scrollToId } from "@/App";
import { SocialIcons } from "@/components/Socials";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Heritage", href: "#heritage" },
  { label: "Fabrics", href: "#fabrics" },
  { label: "Process", href: "#process" },
  { label: "Gallery", href: "#gallery" },
  { label: "Markets", href: "#markets" },
  { label: "Contact", href: "#contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const openSamples = useContext(SampleModalContext);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
        scrolled ? "border-b border-gold/20 bg-navy/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex h-[72px] max-w-[1560px] items-center justify-between gap-6 px-5 sm:px-8 lg:h-[88px]">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex shrink-0 items-center gap-2 text-left sm:gap-3"
        >
          <img
            src="/logo.webp"
            alt="P.A. International logo"
            className="h-12 w-auto rounded-sm bg-white p-1 min-[1180px]:h-[60px]"
          />
          <span
            data-testid="nav-wordmark"
            className="wordmark whitespace-nowrap text-[22px] uppercase leading-none tracking-[0.04em] text-champagne min-[1180px]:text-[28px]"
          >
            International
          </span>
        </button>

        <nav className="hidden items-center justify-end gap-5 min-[1180px]:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-200 transition-colors hover:text-gold"
            >
              {l.label}
            </button>
          ))}
          <SocialIcons
            className="hidden 2xl:flex"
            itemClassName="h-7 w-7 border-0 hover:translate-y-0"
            size={15}
            testPrefix="nav-social"
          />
          <button
            data-testid="nav-request-samples-btn"
            onClick={openSamples}
            className="whitespace-nowrap border border-gold/70 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy"
          >
            Request Samples
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-btn"
          className="text-white min-[1180px]:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-navy/95 backdrop-blur-md min-[1180px]:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left text-sm font-semibold uppercase tracking-[0.2em] text-slate-200"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-request-samples-btn"
                onClick={() => {
                  setOpen(false);
                  openSamples();
                }}
                className="mt-2 border border-gold/70 px-5 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-gold"
              >
                Request Samples
              </button>
              <SocialIcons className="mt-4 border-t border-white/10 pt-4" testPrefix="nav-mobile-social" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
